import type { UserAccessLevel } from "@/types";

/**
 * Access-level ladder, mirroring the backend's `has_minimum_access_level`
 * (`backend/app/core/auth_helpers.py`). Each level is a superset of the one
 * below it for global purposes.
 */
export const ACCESS_LEVEL_RANK: Record<UserAccessLevel, number> = {
  GUEST: 1,
  INTERNAL: 2,
  INTERNAL_PLUS: 3,
  ADMIN: 4,
};

/** The floors a route can declare. Pages open to every authenticated user
 * simply have no entry in ROUTE_ACCESS. */
export type RouteAccessLevel = "INTERNAL" | "ADMIN";

/**
 * Minimum access level per protected page — the single source of truth for
 * both the route guards (`App.tsx` → `ProtectedRoute`) and which items the
 * sidebar renders (`Sidebar.tsx`). Keeping one map means a nav item and the
 * route behind it can never disagree; previously the sidebar showed every
 * page to everyone and a GUEST clicking "Käyttäjähallinta" got a permission
 * toast and a bounce back to /dashboard.
 *
 * Floors match the backend's GLOBAL_CAPABILITIES (see
 * `docs/access_management/index.md`): MANAGE_USERS is ADMIN-only,
 * MANAGE_SUBSTITUTIONS and MANAGE_TEMPLATES have an INTERNAL floor.
 *
 * Routes deliberately absent (open to any authenticated user): /dashboard,
 * /tasks, /overview, /projects, /schedule. A GUEST reaches these but the
 * backend scopes their visible projects to the empty set, so they render
 * empty rather than being forbidden.
 *
 * /feedback has an INTERNAL floor because Kehitys is an internal page: a GUEST
 * sees no reports at all. Voting only means something if the board is readable,
 * so read and write are gated together — a write-only page would mean filing a
 * report and never seeing the answer. Backend counterpart:
 * GLOBAL_CAPABILITIES[USE_FEEDBACK].
 */
export const ROUTE_ACCESS = {
  "/feedback": "INTERNAL",
  "/substitutes": "INTERNAL",
  "/task-templates": "INTERNAL",
  "/users": "ADMIN",
} as const satisfies Record<string, RouteAccessLevel>;

/** Whether `level` reaches `required` on the ladder above. */
export function meetsAccessLevel(
  level: UserAccessLevel,
  required: RouteAccessLevel,
): boolean {
  return ACCESS_LEVEL_RANK[level] >= ACCESS_LEVEL_RANK[required];
}

/** The floor declared for `path`, or undefined when the page is open. */
export function routeAccessLevel(path: string): RouteAccessLevel | undefined {
  return (ROUTE_ACCESS as Record<string, RouteAccessLevel | undefined>)[path];
}

/**
 * Whether a user at `accessLevel` may reach `path`.
 *
 * Fails **closed** on an unknown access level: `dbUser` is null until
 * `syncCurrentUser` resolves, and during that window a restricted item is
 * hidden rather than shown-then-removed. `ProtectedRoute` deliberately does
 * the opposite for navigation (see its `dbUser &&` guard) so a slow sync
 * never bounces a legitimate user off the page they asked for.
 */
export function canAccessRoute(
  accessLevel: UserAccessLevel | null | undefined,
  path: string,
): boolean {
  const required = routeAccessLevel(path);
  if (!required) return true;
  if (!accessLevel) return false;
  return meetsAccessLevel(accessLevel, required);
}
